import { Change } from '@adornis/base/change.js';
import { A } from '@adornis/base/env-info.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { resolveFieldOnEntity } from '@adornis/baseql/entities/field-path-resolution.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { getAutofillFunction } from '@adornis/form-auto-filler/client/form-autofiller.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import type { useGeneratedForm } from '@adornis/generated-form/use-generated-form.js';
import { html, nothing } from 'lit';

type AutofillField = { path: (string | number)[]; label?: string; placeholder?: string };

/** Track the rendered fields without depending on the legacy Form's directive registry. */
export const withFormAutofiller = <T extends AdornisEntity>(generated: ReturnType<typeof useGeneratedForm<T>>) => {
  const fields: AutofillField[] = [];
  const renderField: typeof generated.renderField = (path, renderer) => {
    const field: AutofillField = { path };
    fields.push(field);
    return generated.renderField(
      path,
      renderer &&
        (props => {
          field.label = props.label;
          field.placeholder = props.placeholder;
          return renderer(props);
        }),
    );
  };
  return {
    form: generated.form,
    renderField,
    autofiller: FormAutofiller({ form: generated.form, fields }),
  };
};

const showContextDialog = () =>
  showDialog<string>({
    modalBoxClasses: 'max-w-md',
    content: (resolve, reject) => {
      let contextValue = '';
      return html`
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold">Provide Context for Auto-Fill</h3>
          <div class="divider my-0"></div>
          <p class="text-sm opacity-75">Describe the context for auto-filling this form.</p>
          <textarea
            class="textarea textarea-bordered w-full h-32 focus:textarea-primary"
            placeholder="Enter context for the AI to use when generating form values..."
            @input=${(event: Event) => {
              if (event.target instanceof HTMLTextAreaElement) contextValue = event.target.value;
            }}
          ></textarea>
          <div class="text-xs opacity-60 italic pb-2">
            Pro tip: Hold Shift when clicking Auto-Fill to skip this dialog
          </div>
          <div class="modal-action justify-end gap-2">
            <button @click=${() => reject()} class="btn btn-outline btn-sm">Cancel</button>
            <button @click=${() => resolve(contextValue)} class="btn btn-primary btn-sm">Start Auto-Fill</button>
          </div>
        </div>
      `;
    },
  });

const FormAutofiller = stateful(
  <T extends AdornisEntity>({ form, fields }: { form: FunctionalForm<T>; fields: AutofillField[] }) => {
    const [loading, setLoading] = useState(false);
    if (A.isProduction) return nothing;

    const autofill = async (event: MouseEvent) => {
      if (loading) return;
      let userContext: string | undefined;
      if (!event.shiftKey) {
        try {
          userContext = await showContextDialog();
        } catch {
          return;
        }
      }
      setLoading(true);
      try {
        for (const { path, label, placeholder } of fields) {
          await new Promise<void>(resolve => setTimeout(resolve, 100));
          try {
            const definition = resolveFieldOnEntity<AdornisEntity>(form.document, path);
            if (!definition) continue;
            const contextInfo = JSON.stringify({
              field: `${form.document._class}.${definition.name}`,
              label: label || definition.name,
              placeholder: placeholder || '',
              parentContext: form.document._class,
              path: path.join('.'),
              userContext,
            });
            const value: unknown = await getAutofillFunction(
              definition,
              form.document._class,
              form.document,
            )(contextInfo)();
            new Change(value, path).applyToContext(form.document);
          } catch (error) {
            console.error('Error autofilling field:', error);
          }
        }
        void XSnackbar.show('Form fields have been auto-filled successfully');
      } catch (error) {
        console.error('Error during form autofill:', error);
        void XSnackbar.show('Failed to auto-fill form fields');
      } finally {
        setLoading(false);
      }
    };

    return html`
      <button
        type="button"
        @click=${autofill}
        class="fixed top-20 right-0 z-[999] btn ${loading ? 'btn-disabled' : 'btn-primary'} btn-sm rounded-r-none rounded-l-lg shadow-lg flex items-center gap-1"
        title=${loading ? 'Auto-fill in progress...' : 'Click to auto-fill form fields (Shift+Click to skip context)'}
        ?disabled=${loading}
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z"
          />
        </svg>
        <span>${loading ? 'Auto-filling...' : 'Auto-fill'}</span>
      </button>
    `;
  },
);
